import { useState, useEffect, useMemo, useCallback } from 'react'
import { collection, getDocs, doc, updateDoc, setDoc, deleteDoc, deleteField, writeBatch, query, where } from 'firebase/firestore'
import { Users, Search, ShieldCheck, ShieldOff, UserPlus, Clock, AlertTriangle } from 'lucide-react'
import AdminNav from '../components/AdminNav'
import { db, APP_COLLECTION, APP_DOC } from '../lib/firebase'
import { useConfig } from '../context/ConfigContext'
import { useAuth } from '../context/AuthContext'
import { degreesForPerson, coursesForDegrees, findDegree } from '../data/degrees'
import { buildRoster, parseEmailList, newEmailsOnly, normaliseEmail } from '../data/roster'
import {
  managerMap, wouldCycle, chainsAfterChange, chainRepairs, expectedChain, CycleError,
} from '../data/managerChain'

/**
 * Who is who: department, role, manager, and any degree awarded to somebody
 * individually.
 *
 * Everything on this page describes a PERSON. What a degree contains lives on
 * the Degrees page, and which degrees a department confers lives on
 * Departments — all three used to be buttons crammed into a row of the trainee
 * table that was also trying to show progress.
 *
 * The Enrolled-in column is read-only on purpose: it is worked out from the
 * department mapping plus individual awards, so showing it as an editable
 * control would invite someone to fix an enrolment here that is really a
 * mapping problem on another page.
 */
export default function AdminPeoplePage() {
  const { degrees, departments } = useConfig()
  const { user: viewer } = useAuth()
  const [people, setPeople] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [busy, setBusy] = useState(null)
  const [error, setError] = useState(null)
  const [draft, setDraft] = useState('')
  const [draftDepartment, setDraftDepartment] = useState('')
  const [added, setAdded] = useState(null)
  // Roles chosen for people who have not signed in yet. Admin-readable only;
  // a manager sees nothing here and cannot set one.
  const [pendingRoles, setPendingRoles] = useState({})
  const isAdmin = viewer?.isAdmin === true
  const isManager = viewer?.isManager === true && !isAdmin

  const load = useCallback(async () => {
    try {
      let userDocs = []
      let assignmentDocs = []

      if (isAdmin) {
        // Two populations: people with an account, and people an admin has
        // enrolled who have not signed in yet. Merged on email.
        const [userSnap, assignmentSnap] = await Promise.all([
          getDocs(collection(db, APP_COLLECTION, APP_DOC, 'users')),
          getDocs(collection(db, APP_COLLECTION, APP_DOC, 'assignments')),
        ])
        userDocs = userSnap.docs
        assignmentDocs = assignmentSnap.docs

        const pendingSnap = await getDocs(collection(db, APP_COLLECTION, APP_DOC, 'pending_roles'))
        setPendingRoles(Object.fromEntries(pendingSnap.docs.map((d) => [d.id, d.data()])))
      } else if (isManager) {
        const usersCol = collection(db, APP_COLLECTION, APP_DOC, 'users')
        const assignmentsCol = collection(db, APP_COLLECTION, APP_DOC, 'assignments')

        const [directUsers, belowUsers, directAssign, belowAssign] = await Promise.allSettled([
          getDocs(query(usersCol, where('managedBy', '==', viewer.uid))),
          getDocs(query(usersCol, where('managerChain', 'array-contains', viewer.uid))),
          getDocs(query(assignmentsCol, where('managedBy', '==', viewer.uid))),
          getDocs(query(assignmentsCol, where('managerChain', 'array-contains', viewer.uid))),
        ])

        const userMap = new Map()
        for (const res of [directUsers, belowUsers]) {
          if (res.status === 'fulfilled') {
            for (const d of res.value.docs) userMap.set(d.id, d)
          }
        }
        userDocs = [...userMap.values()]

        const assignMap = new Map()
        for (const res of [directAssign, belowAssign]) {
          if (res.status === 'fulfilled') {
            for (const d of res.value.docs) assignMap.set(d.id, d)
          }
        }
        assignmentDocs = [...assignMap.values()]
      }

      setPeople(buildRoster(
        userDocs.map((d) => ({ uid: d.id, ...d.data() })),
        assignmentDocs.map((d) => ({ email: d.id, ...d.data() }))
      ))
    } catch (err) {
      console.error('Failed to load people:', err)
      setError('Could not load the roster.')
    } finally {
      setLoading(false)
    }
  }, [isAdmin, isManager, viewer?.uid])

  useEffect(() => { load() }, [load])

  /** Department and degrees live in the assignment, keyed by email. */
  async function assign(person, changes) {
    setBusy(person.email)
    setError(null)
    try {
      const ref = doc(db, APP_COLLECTION, APP_DOC, 'assignments', normaliseEmail(person.email))
      // Whole-document write, so every field has to be listed or it is
      // deleted. managerChain is RECOMPUTED from managedBy rather than copied
      // off the roster row (I4): echoing client state here let a department
      // edit re-commit a stale chain, which sign-in then adopted onto the
      // account — quietly restoring a manager who had been removed.
      const next = {
        department: person.department || null,
        degrees: person.degrees || [],
        managedBy: person.managedBy || null,
        managerChain: expectedChain(person, managerMap(people)),
        ...changes,
      }
      if (!next.department && !next.managedBy && (!next.degrees || next.degrees.length === 0) && person.signedIn) {
        // Nothing left to say about them: drop the record rather than keep an
        // empty one that makes the roster look fuller than it is.
        await deleteDoc(ref)
      } else {
        await setDoc(ref, next)
      }
      await load()
    } catch (err) {
      console.error('Failed to update assignment:', err)
      setError('That did not save — check your permissions.')
    } finally {
      setBusy(null)
    }
  }

  /**
   * Write a set of chain rows in ONE batch.
   *
   * I3. These are permission records: a manager is removed by taking them out
   * of a chain, so a half-applied subtree is a half-applied revocation — the
   * removed manager keeps reading a trainee's answers, indefinitely, with the
   * console showing the change as done. `Promise.all` cannot give that
   * guarantee; a batch can. Firestore caps a batch at 500 operations and this
   * writes at most two per person, so it holds to ~250 people in one subtree.
   */
  async function writeChains(rows, subjectEmail = null) {
    const batch = writeBatch(db)
    for (const row of rows) {
      const isSubject = subjectEmail && row.email === subjectEmail
      // The assignment records the DECISION, so it is written for the person
      // being edited even if they had no assignment. Descendants only get one
      // if they already had one — creating assignments for people nobody
      // touched would mark them enrolled as a side effect.
      const ref = doc(db, APP_COLLECTION, APP_DOC, 'assignments', normaliseEmail(row.email))
      if (isSubject && (row.assigned || row.managedBy)) {
        // The subject's whole assignment is rewritten, so every field is
        // restated or it is deleted.
        const source = people.find((p) => p.email === row.email)
        batch.set(ref, {
          department: source?.department || null,
          degrees: source?.degrees || [],
          managedBy: row.managedBy,
          managerChain: row.managerChain,
        })
      } else if (row.assigned) {
        // A DESCENDANT only needs their chain corrected. Rewriting their whole
        // assignment restated `department` from the merged roster row, which
        // falls through to the user document when the assignment has none — so
        // moving one person's manager quietly persisted a legacy department
        // onto people nobody had touched, and that in turn blocked the
        // "nothing left to say about them" cleanup, which keys off it.
        batch.update(ref, { managerChain: row.managerChain })
      }
      if (row.uid) {
        batch.update(doc(db, APP_COLLECTION, APP_DOC, 'users', row.uid), {
          managedBy: row.managedBy || deleteField(),
          managerChain: row.managerChain,
        })
      }
    }
    await batch.commit()
  }

  /**
   * Change who manages somebody, and fix every chain that invalidates.
   */
  async function setManager(person, managedBy) {
    setBusy(person.email)
    setError(null)
    try {
      const rows = chainsAfterChange(people, person.uid, managedBy, person.email)

      // Clearing the last thing an assignment said about somebody removes the
      // record rather than leaving an empty shell — the behaviour the old
      // per-field handler had, which the first attempt dropped.
      const subject = rows.find((r) => r.email === person.email)
      if (subject && !managedBy && !person.department &&
          (!person.degrees || person.degrees.length === 0) && person.signedIn) {
        const batch = writeBatch(db)
        batch.delete(doc(db, APP_COLLECTION, APP_DOC, 'assignments', normaliseEmail(person.email)))
        if (person.uid) {
          batch.update(doc(db, APP_COLLECTION, APP_DOC, 'users', person.uid),
            { managedBy: deleteField(), managerChain: [] })
        }
        for (const row of rows.filter((r) => r.email !== person.email)) {
          if (row.uid) {
            batch.update(doc(db, APP_COLLECTION, APP_DOC, 'users', row.uid),
              { managerChain: row.managerChain })
          }
          if (row.assigned) {
            batch.update(doc(db, APP_COLLECTION, APP_DOC, 'assignments', normaliseEmail(row.email)),
              { managerChain: row.managerChain })
          }
        }
        await batch.commit()
      } else {
        await writeChains(rows, person.email)
      }
      await load()
    } catch (err) {
      console.error('Failed to save the manager:', err)
      setError(err instanceof CycleError
        ? `${person.displayName || person.email} already manages that person, directly or further down.`
        : `Nothing was saved for ${person.displayName || person.email} — the change was rejected, so the hierarchy is unchanged.`)
      await load()
    } finally {
      setBusy(null)
    }
  }

  /**
   * Rebuild every chain that disagrees with `managedBy`.
   *
   * The backfill for existing data, and the repair when a write failed.
   */
  async function rebuildChains() {
    setBusy('rebuilding')
    setError(null)
    try {
      const rows = chainRepairs(people)
      if (rows.length) await writeChains(rows)
      await load()
    } catch (err) {
      console.error('Failed to rebuild the chains:', err)
      setError('Nothing was written — the rebuild was rejected, so the chains are as they were.')
      await load()
    } finally {
      setBusy(null)
    }
  }

  /** Role and manager belong to the account, so they still live on the user. */
  /**
   * Set somebody's role, whether or not they have ever signed in.
   *
   * pending_roles is written EVERY time, not just for people without an
   * account. It is what a fresh sign-in copies from, so leaving it stale after
   * a demotion would let the next sign-in restore the role that was just taken
   * away — the rules compare both fields against it precisely so that a
   * half-stale record cannot grant anything, and this is the write that keeps
   * it whole.
   */
  async function setEntitlement(person, { role, isAdmin: admin }) {
    setBusy(person.email)
    setError(null)
    try {
      const email = normaliseEmail(person.email)
      await setDoc(doc(db, APP_COLLECTION, APP_DOC, 'pending_roles', email), {
        role: role ?? null,
        isAdmin: admin === true,
        setBy: viewer?.email || '',
        setAt: new Date().toISOString(),
      })
      if (person.uid) {
        await updateDoc(doc(db, APP_COLLECTION, APP_DOC, 'users', person.uid), {
          role: role ?? deleteField(),
          isAdmin: admin === true,
        })
      }
      await load()
    } catch (err) {
      console.error('Failed to set the role:', err)
      setError('That did not save — check you still have admin rights.')
    } finally {
      setBusy(null)
    }
  }

  /**
   * Promote somebody to admin.
   *
   * `role: 'admin'` rides along with the flag because both already exist on the
   * accounts that have it, and AuthContext reads the role for display while the
   * rules test isAdmin — leaving one of them out gives a console that renders
   * for a person whose every read is then refused.
   *
   * Confirmed rather than one-click: an admin sees every trainee's answers and
   * can reset anyone's progress, and unlike the manager toggle beside it there
   * is nothing narrower to fall back to.
   */
  async function grantAdmin(person) {
    const who = person.displayName || person.email
    const when = person.signedIn
      ? ''
      : '\n\nThey have never signed in, so this waits and is applied the moment they do.'
    if (!window.confirm(
      `Make ${who} an admin?\n\nAdmins see every trainee's scores and answers, can reset anyone's progress, and can grant admin to others.${when}`
    )) return
    await setEntitlement(person, { role: 'admin', isAdmin: true })
  }

  /**
   * Take admin away.
   *
   * Refuses to remove the last one. There is no other way back in: entitlement
   * is only writable by an admin, so an app with none needs somebody with
   * Firebase console access to repair it by hand.
   */
  async function revokeAdmin(person) {
    const admins = people.filter((p) => p.isAdmin === true)
    if (admins.length <= 1 && person.isAdmin) {
      setError('That is the only admin left. Make somebody else an admin first — with none, only the Firebase console can let anyone back in.')
      return
    }
    const who = person.displayName || person.email
    if (!window.confirm(`Remove admin rights from ${who}?\n\nThey become an ordinary member — set them back to manager afterwards if that is what you meant.`)) return
    await setEntitlement(person, { role: null, isAdmin: false })
  }

  async function addPeople() {
    const { valid, invalid } = parseEmailList(draft)
    const fresh = newEmailsOnly(valid, people)
    if (!fresh.length) {
      setAdded({ added: 0, already: valid.length - fresh.length, invalid })
      if (!invalid.length && valid.length) setDraft('')
      return
    }
    setBusy('adding')
    setError(null)
    try {
      await Promise.all(fresh.map((email) =>
        setDoc(doc(db, APP_COLLECTION, APP_DOC, 'assignments', email), {
          department: draftDepartment || null,
          degrees: [],
          managedBy: isManager ? viewer.uid : null,
          managerChain: isManager ? [viewer.uid] : [],
        })))
      setDraft('')
      setAdded({ added: fresh.length, already: valid.length - fresh.length, invalid })
      await load()
    } catch (err) {
      console.error('Failed to add people:', err)
      setError('Could not add those — check your permissions.')
    } finally {
      setBusy(null)
    }
  }

  async function removeAssignment(person) {
    if (!window.confirm(
      `Remove ${person.email} from ${person.department || 'their department'}?` +
      (person.signedIn
        ? '\n\nTheir account and every completed module stay exactly as they are — they simply stop being enrolled in anything.'
        : '\n\nThey have never signed in, so this removes them from the list entirely.')
    )) return
    setBusy(person.email)
    try {
      await deleteDoc(doc(db, APP_COLLECTION, APP_DOC, 'assignments', normaliseEmail(person.email)))
      await load()
    } catch (err) {
      console.error('Failed to remove assignment:', err)
      setError('That did not save — check you still have admin rights.')
    } finally {
      setBusy(null)
    }
  }

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return people
    return people.filter((p) =>
      (p.displayName || '').toLowerCase().includes(q) || (p.email || '').toLowerCase().includes(q))
  }, [people, searchQuery])

  // Only somebody with an account can manage anyone.
  const managers = people.filter((p) => p.uid && (p.role === 'manager' || p.isAdmin === true))
  const waiting = people.filter((p) => !p.signedIn).length
  const managerOf = useMemo(() => managerMap(people), [people])
  // Reported, never silently repaired on read: a chain that fixed itself on
  // every page load would hide the fact that a write had failed.
  const repairs = useMemo(() => chainRepairs(people), [people])

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8">
        <AdminNav />
        <div className="flex justify-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <AdminNav />

      <div className="mb-6 flex items-start gap-3">
        <Users className="mt-1 h-6 w-6 text-purple-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isManager ? 'My Team' : 'Crew'}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {isManager
              ? 'Manage your team members, assign departments or degrees, and add new direct reports.'
              : 'Put someone in a department and they are enrolled in whatever it confers. Everything else here is an exception to that.'}
          </p>
        </div>
      </div>

      {error && (
        <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
      )}

      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-800">
          <UserPlus className="h-4 w-4 text-purple-600" /> {isManager ? 'Add team members' : 'Add people'}
        </h2>
        <p className="mt-1 text-xs text-gray-500">
          {isManager
            ? 'Paste one address or a whole column of them. They will be added to your team and their courses will be waiting the first time they sign in.'
            : 'Paste one address or a whole column of them. They are enrolled straight away and their courses are waiting the first time they sign in — no invitation is sent, and none is needed: anyone with a Finder account can already open the app.'}
        </p>

        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <textarea
            value={draft}
            onChange={(e) => { setDraft(e.target.value); setAdded(null) }}
            rows={draft.includes('\n') ? 4 : 1}
            placeholder="jane.smith@finder.com, tim.brown@finder.com"
            className="flex-1 resize-y rounded-lg border border-gray-200 px-3 py-2 font-mono text-sm focus:border-blue-300 focus:outline-none"
          />
          <div className="flex gap-2 sm:flex-col">
            <select
              value={draftDepartment}
              onChange={(e) => setDraftDepartment(e.target.value)}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-blue-300 focus:outline-none"
            >
              <option value="">No department</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            <button
              onClick={addPeople}
              disabled={busy === 'adding' || !draft.trim()}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-700 disabled:bg-gray-300"
            >
              <UserPlus className="h-4 w-4" />
              {busy === 'adding' ? 'Adding…' : 'Add'}
            </button>
          </div>
        </div>

        {added && (
          <div className="mt-3 space-y-1 text-xs">
            {added.added > 0 && (
              <p className="text-green-700">
                Added {added.added} {added.added === 1 ? 'person' : 'people'}
                {draftDepartment ? ` to ${departments.find((d) => d.id === draftDepartment)?.name}` : ' with no department yet'}
                {isManager ? ' (assigned to your team)' : ''}.
              </p>
            )}
            {added.already > 0 && (
              <p className="text-gray-500">
                {added.already} {added.already === 1 ? 'was' : 'were'} already on the list — left alone.
              </p>
            )}
            {added.invalid.length > 0 && (
              <p className="text-amber-700">
                <AlertTriangle className="mr-1 inline h-3 w-3" />
                Skipped {added.invalid.length}: {added.invalid.slice(0, 3).join(', ')}
                {added.invalid.length > 3 ? '…' : ''} — only finder.com and findercrew.com addresses can sign in.
              </p>
            )}
          </div>
        )}
      </div>

      {waiting > 0 && (
        <p className="mb-4 text-xs text-gray-500">
          {waiting} {waiting === 1 ? 'person has' : 'people have'} been enrolled but never opened the
          app. Nothing is wrong — their courses are simply waiting.
        </p>
      )}

      {repairs.length > 0 && isAdmin && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
          <p className="flex-1 text-xs text-amber-900">
            <strong>{repairs.length} {repairs.length === 1 ? 'person needs' : 'people need'} their
            manager chain rebuilt.</strong> Their own manager still sees them; anyone further up
            does not. Normal the first time — after that it means a change did not finish saving.
          </p>
          <button
            onClick={rebuildChains}
            disabled={busy === 'rebuilding'}
            className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:bg-gray-300"
          >
            {busy === 'rebuilding' ? 'Rebuilding…' : 'Rebuild chains'}
          </button>
        </div>
      )}

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name or email…"
          className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-4 text-sm focus:border-blue-300 focus:outline-none"
        />
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left text-gray-600">
              <th className="px-4 py-3 font-medium">Person</th>
              <th className="px-4 py-3 font-medium">Department</th>
              <th className="px-4 py-3 font-medium">Also awarded</th>
              <th className="px-4 py-3 font-medium">Enrolled in</th>
              <th className="px-4 py-3 font-medium">Manager</th>
              <th className="whitespace-nowrap px-4 py-3 font-medium">Role</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((person) => {
              const held = degreesForPerson(person, departments, degrees)
              const conferred = departments.find((d) => d.id === person.department)?.degrees || []
              const courses = coursesForDegrees(held, degrees).length
              const isSelf = person.uid === viewer?.uid
              return (
                <tr key={person.email} className={`border-b border-gray-100 last:border-0 ${busy === person.email ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900">
                        {person.displayName || person.email.split('@')[0]}
                      </p>
                      {!person.signedIn && (
                        <span
                          title="Enrolled, but has never opened the app. Their courses are waiting for them."
                          className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
                        >
                          <Clock className="h-3 w-3" /> not signed in
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400">{person.email}</p>
                  </td>

                  <td className="px-4 py-3">
                    <select
                      value={person.department || ''}
                      onChange={(e) => assign(person, { department: e.target.value || null })}
                      className="rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 focus:border-blue-300 focus:outline-none"
                    >
                      <option value="">None</option>
                      {departments.map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {degrees.map((degree) => {
                        const individually = (person.degrees || []).includes(degree.id)
                        const fromDept = conferred.includes(degree.id)
                        return (
                          <button
                            key={degree.id}
                            disabled={fromDept}
                            title={fromDept
                              ? `Already conferred by their department`
                              : individually ? `Remove the ${degree.name} degree` : `Award the ${degree.name} degree on top of their department`}
                            onClick={() => assign(person, {
                              degrees: individually
                                ? (person.degrees || []).filter((d) => d !== degree.id)
                                : [...(person.degrees || []), degree.id],
                            })}
                            className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${
                              fromDept
                                ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                                : individually
                                  ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                                  : 'bg-white text-gray-500 ring-1 ring-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            {degree.name}
                          </button>
                        )
                      })}
                    </div>
                  </td>

                  <td className="px-4 py-3 text-xs text-gray-500">
                    {held.length === 0
                      ? <span className="text-gray-400">Nothing yet</span>
                      : (
                        <>
                          {held.map((id) => findDegree(degrees, id)?.name || id).join(', ')} · {courses} course{courses === 1 ? '' : 's'}
                          {person.assigned && (
                            <button
                              onClick={() => removeAssignment(person)}
                              className="ml-2 whitespace-nowrap text-[11px] font-medium text-red-600 hover:underline"
                              title="Remove their department and any individually awarded degree"
                            >
                              Unenrol
                            </button>
                          )}
                        </>
                      )}
                  </td>

                  <td className="px-4 py-3">
                    {isAdmin ? (
                      <select
                        value={person.managedBy || ''}
                        title={person.signedIn
                          ? ''
                          : 'Set it now — it is applied the moment they first sign in'}
                        onChange={(e) => setManager(person, e.target.value || null)}
                        disabled={busy !== null}
                        className="rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 focus:border-blue-300 focus:outline-none"
                      >
                        <option value="">None</option>
                        {managers
                          .filter((m) => m.uid !== person.uid && !wouldCycle(person.uid, m.uid, managerOf))
                          .map((m) => (
                            <option key={m.uid} value={m.uid}>{m.displayName || m.email}</option>
                          ))}
                      </select>
                    ) : (
                      <span className="text-xs text-gray-600">
                        {managers.find((m) => m.uid === person.managedBy)?.displayName ||
                         managers.find((m) => m.uid === person.managedBy)?.email ||
                         (person.managedBy === viewer?.uid ? 'You' : 'Assigned')}
                      </span>
                    )}
                  </td>

                  <td className="px-4 py-3">
                    {(() => {
                      // A role can be set before the person has ever signed in:
                      // it waits in pending_roles and is adopted on their first
                      // sign-in. So the cell shows the role they HAVE, or the
                      // one waiting for them, and says which.
                      const pending = pendingRoles[normaliseEmail(person.email)] || null
                      const effectiveAdmin = person.isAdmin === true ||
                        (!person.signedIn && pending?.isAdmin === true)
                      const effectiveRole = person.role ||
                        (!person.signedIn ? pending?.role || null : null)
                      const waiting = !person.signedIn && (effectiveRole || effectiveAdmin)

                      const applied = waiting
                        ? ' — applied the moment they first sign in'
                        : ''

                      if (effectiveAdmin) {
                        return isAdmin ? (
                          <button
                            onClick={() => revokeAdmin(person)}
                            disabled={isSelf || busy !== null}
                            className="inline-flex items-center gap-1 rounded bg-purple-100 px-1.5 py-0.5 text-[11px] font-medium text-purple-700 hover:bg-purple-200 disabled:opacity-60 disabled:hover:bg-purple-100"
                            title={isSelf
                              ? 'You cannot remove your own admin rights'
                              : `Remove admin rights${waiting ? ' before they are ever applied' : ''}`}
                          >
                            <ShieldOff className="h-3 w-3" /> Admin{waiting ? ' (pending)' : ''}
                          </button>
                        ) : (
                          <span className="rounded bg-purple-100 px-1.5 py-0.5 text-[11px] font-medium text-purple-700">
                            Admin{waiting ? ' (pending)' : ''}
                          </span>
                        )
                      }
                      if (effectiveRole === 'manager') {
                        return isAdmin ? (
                          <span className="inline-flex items-center gap-1">
                            <button
                              onClick={() => setEntitlement(person, { role: null, isAdmin: false })}
                              disabled={busy !== null}
                              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                              title={waiting ? 'Waiting for their first sign-in — click to cancel' : 'Remove the manager role'}
                            >
                              <ShieldOff className="h-3 w-3" /> Manager{waiting ? ' (pending)' : ''}
                            </button>
                            <button
                              onClick={() => grantAdmin(person)}
                              disabled={busy !== null}
                              className="rounded px-1.5 py-0.5 text-[11px] font-medium text-purple-600 hover:bg-purple-50 disabled:text-gray-300"
                              title={`Promote to admin${applied}`}
                            >
                              → Admin
                            </button>
                          </span>
                        ) : (
                          <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[11px] font-medium text-blue-700">Manager</span>
                        )
                      }
                      return isAdmin ? (
                        <span className="inline-flex items-center gap-1">
                          <button
                            onClick={() => setEntitlement(person, { role: 'manager', isAdmin: false })}
                            disabled={isSelf || busy !== null}
                            className="inline-flex items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium text-blue-600 hover:bg-blue-50 disabled:text-gray-300"
                            title={isSelf ? 'You are already an admin' : `Make this person a manager${applied}`}
                          >
                            <ShieldCheck className="h-3 w-3" /> Make manager
                          </button>
                          <button
                            onClick={() => grantAdmin(person)}
                            disabled={isSelf || busy !== null}
                            className="rounded px-1.5 py-0.5 text-[11px] font-medium text-purple-600 hover:bg-purple-50 disabled:text-gray-300"
                            title={isSelf ? 'You are already an admin' : `Make this person an admin${applied}`}
                          >
                            → Admin
                          </button>
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Member</span>
                      )
                    })()}
                  </td>
                </tr>
              )
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                  {searchQuery ? 'Nobody matches that search' : isManager ? 'No team members added yet' : 'Nobody has signed in yet'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="mt-4 text-xs text-gray-400">
        A degree greyed out under “Also awarded” is one their department already confers — change it
        on the Departments page rather than per person.
      </p>
    </div>
  )
}
